/// <amd-module name="Repos/Views/Settings/Branches/Services/BranchesService" />
import { EnhancedGitRef } from "Data/EnhancedGitRef";
import { GitRef } from "Repos/Common/Contracts/Git";
import { refToBranchName } from "Repos/Common/Util/Ref";
import { SparseTreeProvider } from "Repos/SparseTree/SparseTreeProvider";
import { ISparseTreeProviderFetchResult, SparseTreeNode } from "Repos/SparseTree/SparseTreeProvider.Types";
import { ISettingsViewService } from "Repos/Views/Settings/Common/Services/SettingsViewService";
import { IVssPageContext, Services, VssService } from "VSS/Platform/Context";
import { IVssContributionService } from "VSS/Platform/Contribution";
import { IVssPerformanceService } from "VSS/Platform/Performance";
import { ITreeItemEx } from "VSSUI/Utilities/TreeItemProvider";
import { BranchTreeProvider } from "../Provider/BranchTreeProvider";
import { IBranchTreeProvider } from "../Provider/BranchTreeProvider.Types";
import { IBranchesProviderData, IBranchesQuery, IBranchesService } from "./BranchesService.Types";

interface EnhanceResult {
    branches: EnhancedGitRef[];
    compareBranch?: EnhancedGitRef;
    defaultBranch?: EnhancedGitRef;
}

interface DataProviderParams {
    projectId: string;
    repositoryId: string;
    filterString?: string;
}

interface BranchDataResult {
    branches?: GitRef[];
    fetchAsync?: boolean;
}

const BranchSeparator = "/";
const AllBranchesDataProviderContributionId = "ms.vss-code-web.all-branches-data-provider";

class GitBranchesService extends VssService implements IBranchesService {
    private cache: { [key: string]: Promise<IBranchTreeProvider | undefined> | undefined } = {};
    private settingsSvc: ISettingsViewService;

    public _serviceEnd = (pageContext: IVssPageContext): void => {
        super._serviceEnd(pageContext);
    };

    public _serviceStart = (pageContext: IVssPageContext): void => {
        super._serviceStart(pageContext);
        this.settingsSvc = this.pageContext.getService<ISettingsViewService>("SettingsViewService");
    };

    public getBranchTreeProvider(branchesQuery: IBranchesQuery): Promise<IBranchTreeProvider | undefined> {
        const key = getCacheKey(branchesQuery);

        if (this.cache[key]) {
            return this.cache[key]!;
        }

        // Removes refs/heads/
        const enhancedIdentifierFunction = (enhancedRef: EnhancedGitRef) => refToBranchName(enhancedRef.rootRef.name);

        const treeProvider: IBranchTreeProvider = new BranchTreeProvider(
            new SparseTreeProvider([], enhancedIdentifierFunction, BranchSeparator, 0, 3),
            undefined,
            undefined,
            []
        );

        const performanceService = this.pageContext.getService<IVssPerformanceService>("IVssPerformanceService");
        const fetchBranchProviderDataScenario = performanceService.startScenario("branches-service-fetch-branch-tree-provider-data", false);

        return this.getBranchesData(branchesQuery.projectId, branchesQuery.repositoryId, branchesQuery.filterText)
            .catch(this.settingsSvc.showErrorMessage)
            .then(branchesProviderData => {
                if (!branchesProviderData) {
                    return undefined;
                }

                const branchDataResult = this.parseBranchDataForQuery(branchesQuery, branchesProviderData);
                const branches = branchDataResult.branches || [];
                const asyncFlag = branchDataResult.fetchAsync;

                const enhanceResult = this.enhanceRefs(branches, branchesProviderData);

                if (asyncFlag) {
                    const contributionService = this.pageContext.getService<IVssContributionService>("IVssContributionService");

                    const fetchDataHandler = (item: ITreeItemEx<SparseTreeNode<EnhancedGitRef>>, page: number) => {
                        const performanceService = this.pageContext.getService<IVssPerformanceService>("IVssPerformanceService");
                        const fetchMoreScenario = performanceService.startScenario("branches-service-fetch-more-async", false);
                        return contributionService
                            .getDataAsync(
                                AllBranchesDataProviderContributionId,
                                this.getContributionAsyncParams(page, branchesQuery.repositoryId, item.underlyingItem.data.absoluteIdentifier)
                            )
                            .catch(this.settingsSvc.showErrorMessage)
                            .then((results: IBranchesProviderData) => {
                                const branches = (results && results["Git.Branches.Folder"]) || [];

                                const parsedResults: ISparseTreeProviderFetchResult<EnhancedGitRef> = {
                                    data: this.enhanceRefs(branches, results).branches,
                                    hasMore: results["Git.Branches.Folder.HasMore"] === undefined ? false : results["Git.Branches.Folder.HasMore"]
                                };

                                performanceService.endScenario("Repos", fetchMoreScenario.scenarioName, false);

                                return parsedResults;
                            });
                    };

                    treeProvider.initialize(
                        enhanceResult.compareBranch,
                        enhanceResult.defaultBranch,
                        branchesProviderData["Git.Branches.Policies"] || []
                    );
                    treeProvider.baseProvider.initialize(enhanceResult.branches, 0, fetchDataHandler);
                } else {
                    treeProvider.initialize(
                        enhanceResult.compareBranch,
                        enhanceResult.defaultBranch,
                        branchesProviderData["Git.Branches.Policies"] || []
                    );
                    treeProvider.baseProvider.initialize(enhanceResult.branches, 30);
                }

                performanceService.endScenario("Repos", fetchBranchProviderDataScenario.scenarioName, false);

                return treeProvider;
            });
    }

    public getRequestKey(branchesQuery: IBranchesQuery): string {
        return getCacheKey(branchesQuery);
    }

    private enhanceRefs(branches: GitRef[], branchesData: IBranchesProviderData): EnhanceResult {
        const identifierFunction = (branch: GitRef) => branch.name;
        const performanceService = this.pageContext.getService<IVssPerformanceService>("IVssPerformanceService");
        const enhanceScenario = performanceService.startScenario("branches-service-enhance-branches", false);

        const defaultBranch = branchesData["Git.Branches.Default"];
        const compareBranch = branchesData["Git.Branches.Compare"] || defaultBranch;

        const enhance: (branch: GitRef) => EnhancedGitRef = (baseBranch: GitRef) => {
            const baseIdentifier = identifierFunction(baseBranch);
            const isCompare = compareBranch ? baseIdentifier === identifierFunction(compareBranch) : false;
            const isDefault = defaultBranch ? baseIdentifier === identifierFunction(defaultBranch) : false;

            return {
                rootRef: baseBranch,
                isCompare,
                isDefault,
                ref: baseBranch.name
            };
        };

        const enhancedCompareBranch = compareBranch && enhance(compareBranch);
        const enhancedDefaultBranch = defaultBranch && enhance(defaultBranch);

        const enhancedRefs = branches.map(enhance);
        performanceService.endScenario("Repos", enhanceScenario.scenarioName, false);

        return {
            branches: enhancedRefs,
            compareBranch: enhancedCompareBranch,
            defaultBranch: enhancedDefaultBranch
        };
    }

    private getBranchesData(projectId: string, repositoryId: string, filterString?: string): Promise<IBranchesProviderData | undefined> {
        const contributionService = this.pageContext.getService<IVssContributionService>("IVssContributionService");
        const contributionId = AllBranchesDataProviderContributionId;

        const branchProperties: DataProviderParams = {
            projectId: projectId,
            repositoryId: repositoryId,
            filterString
        };

        const performanceService = this.pageContext.getService<IVssPerformanceService>("IVssPerformanceService");
        const resolveDataProviderScenario = performanceService.startScenario("branches-service-resolve-data-provider", false, undefined, {
            providerId: contributionId
        });

        return contributionService
            .getDataAsync<IBranchesProviderData>(contributionId, branchProperties, filterString !== undefined)
            .catch(this.settingsSvc.showErrorMessage)
            .then(result => {
                if (!result) {
                    return undefined;
                }
                performanceService.endScenario("Repos", resolveDataProviderScenario.scenarioName, false);
                return result;
            });
    }

    private getContributionAsyncParams(page: number, repositoryId: string, folderName?: string) {
        return {
            HasMoreFolder: folderName,
            repositoryId: repositoryId,
            folderPage: folderName ? page : undefined
        };
    }

    private parseBranchDataForQuery(branchesQuery: IBranchesQuery, branchesData: IBranchesProviderData): BranchDataResult {
        if (branchesQuery.filterText) {
            return { branches: branchesData["Git.Branches.Filter"] };
        }

        return { branches: branchesData["Git.Branches.All"], fetchAsync: branchesData["Git.Branches.All.FetchData"] };
    }
}

const serviceName = "ISettingsBranchesService";
export function getBranchesService(context: IVssPageContext) {
    return context.getService<IBranchesService>(serviceName);
}

function getCacheKey(branchesQuery: IBranchesQuery) {
    return `${branchesQuery.projectId}/${branchesQuery.repositoryId}/${branchesQuery.filterText}`;
}

Services.add(serviceName, { serviceFactory: GitBranchesService });
